// pages/hr/wizard/OnboardingWizard.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepIndicator,
  Button,
  Card,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/joy';
import {
  CheckCircle as CheckCircleOutlined,
  User as PersonOutlined,
  IdentificationBadge as BadgeOutlined,
  FileText as DescriptionOutlined,
  Briefcase as WorkOutlined,
  Bank as AccountBalanceOutlined,
  Pencil as DrawOutlined,
  Check as CheckOutlined,
  XCircle as ErrorOutlined,
} from '@phosphor-icons/react';
import {
  usePublicOnboardingSession,
  useSaveStepProgress,
  useCompleteStep,
  useSubmitOnboarding,
} from '../../../hooks/useHr';
import type {
  OnboardingWizardStep,
  PublicOnboardingSession,
  WizardSessionState,
} from '../../../types/hr';
import toast from 'react-hot-toast';

// Step components
import { WelcomeStep } from './steps/WelcomeStep';
import { PersonalDetailsStep } from './steps/PersonalDetailsStep';
import { EmploymentTypeStep } from './steps/EmploymentTypeStep';
import { TaxFormsStep } from './steps/TaxFormsStep';
import { DocumentSigningStep } from './steps/DocumentSigningStep';
import { ReviewStep } from './steps/ReviewStep';
import { CompletionStep } from './steps/CompletionStep';

// How long to wait after the last keystroke before saving. The public autosave
// endpoint is anonymously rate-limited, so a per-keystroke save both burns the
// budget and loses whatever was typed after the failure.
const AUTOSAVE_IDLE_MS = 1500;

const stepIcons: Record<string, React.ReactNode> = {
  welcome: <PersonOutlined />,
  identity: <BadgeOutlined />,
  personal_details: <PersonOutlined />,
  employment_type: <WorkOutlined />,
  tax_forms: <AccountBalanceOutlined />,
  document_signing: <DrawOutlined />,
  documents: <DescriptionOutlined />,
  review: <CheckOutlined />,
  completion: <CheckCircleOutlined />,
};

const getStepComponent = (
  step: OnboardingWizardStep,
  sessionState: WizardSessionState,
  onDataChange: (data: Record<string, unknown>) => void,
  onNext: () => void,
  onBack: () => void,
  isFirstStep: boolean,
  isLastStep: boolean,
  sessionData?: PublicOnboardingSession,
  token?: string
) => {
  const commonProps = {
    step,
    sessionState,
    onDataChange,
    onNext,
    onBack,
    isFirstStep,
    isLastStep,
    // Steps that render org-configured content (documents) need the session
    // payload and token; without them they fall back to placeholder content.
    sessionData,
    token,
  };

  switch (step.type) {
    case 'welcome':
      return <WelcomeStep {...commonProps} />;
    case 'personal_details':
      return <PersonalDetailsStep {...commonProps} />;
    case 'employment_type':
      return <EmploymentTypeStep {...commonProps} />;
    case 'tax_forms':
      return <TaxFormsStep {...commonProps} />;
    case 'document_signing':
      return <DocumentSigningStep {...commonProps} />;
    case 'review':
      return <ReviewStep {...commonProps} />;
    case 'completion':
      return <CompletionStep {...commonProps} />;
    default:
      // Packages can reference a step type this build cannot render. The nav
      // buttons live inside each step component, so without our own here the
      // hire is stuck on this screen with no way forward on desktop.
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography level="title-md" sx={{ mb: 1 }}>
            {step.title || 'This step is not available yet'}
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
            {step.required !== false ? 'This required step is not available. Ask HR to correct your onboarding package.' : 'This optional step is not available. You can continue.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            {!isFirstStep && (
              <Button variant="outlined" color="neutral" onClick={onBack}>
                Back
              </Button>
            )}
            <Button disabled={step.required !== false} onClick={onNext}>Continue</Button>
          </Box>
        </Box>
      );
  }
};

export const OnboardingWizard: React.FC = () => {
  const { orgSlug, sessionToken } = useParams<{ orgSlug: string; sessionToken: string }>();
  const [activeStep, setActiveStep] = useState(0);
  const [stepData, setStepData] = useState<Record<string, Record<string, unknown>>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const hasResumedRef = useRef(false);
  // Edits waiting to be flushed to the server, keyed by step id.
  const pendingRef = useRef<Record<string, Record<string, unknown>>>({});
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: session,
    isLoading,
    error,
    refetch,
  } = usePublicOnboardingSession(sessionToken || '');

  const saveProgress = useSaveStepProgress(sessionToken || '');
  const completeStep = useCompleteStep(sessionToken || '');
  const submitOnboarding = useSubmitOnboarding(sessionToken || '');

  // Seed the form from whatever the hire already saved. This runs once per
  // session load: re-seeding on every refetch would fight the user's typing.
  useEffect(() => {
    if (!session || hasResumedRef.current) return;
    hasResumedRef.current = true;

    setStepData(session.step_data || {});
    if (session.status === 'awaiting_review') setSubmitted(true);

    // `current_step` holds the step id the hire last worked on. Fall back to the
    // first step they have not completed, so a closed tab reopens where they were.
    const byCurrent = session.steps.findIndex((s) => s.id === session.current_step);
    const completed = new Set(session.completed_step_ids || []);
    const firstUnfinished = session.steps.findIndex((s) => !completed.has(s.id));
    const resumeIndex = byCurrent >= 0 ? byCurrent : firstUnfinished;
    if (resumeIndex > 0) {
      setActiveStep(resumeIndex);
      setResumed(true);
    }
  }, [session]);

  // Build session state for step components
  const sessionState: WizardSessionState = {
    session_id: session?.id || '',
    team_member_id: session?.team_member_id || '',
    // Joined for greetings and signatures only. Steps that need the parts must
    // read them below: splitting this back apart on the first space turns
    // "Mary Jo Smith" into first "Mary", last "Jo Smith".
    team_member_name: `${session?.team_member_first_name || ''} ${session?.team_member_last_name || ''}`,
    team_member_first_name: session?.team_member_first_name || '',
    team_member_last_name: session?.team_member_last_name || '',
    team_member_phone: session?.team_member_phone || '',
    team_member_email: session?.team_member_email || '',
    member_type: session?.member_type || 'w2',
    organization_name: session?.organization_name || '',
    current_step: activeStep,
    step_data: stepData,
    step_data_by_type: (session?.steps || []).reduce<Record<string, Record<string, unknown>>>(
      (acc, s) => {
        if (stepData[s.id]) acc[s.type] = stepData[s.id];
        return acc;
      },
      {}
    ),
    // Submitting moves the session to awaiting_review; from the hire's side that
    // is done, so treat it as complete.
    is_complete: session?.status === 'completed' || session?.status === 'awaiting_review',
  };

  // Send everything buffered so far. Anything that fails goes back into the
  // buffer so the next flush retries it instead of dropping what the hire typed.
  const flushPending = useCallback(async () => {
    const entries = Object.entries(pendingRef.current);
    if (entries.length === 0) return;
    pendingRef.current = {};

    try {
      for (const [stepId, data] of entries) {
        await saveProgress.mutateAsync({ stepId, data });
      }
      setSaveFailed(false);
    } catch {
      for (const [stepId, data] of entries) {
        pendingRef.current[stepId] = { ...data, ...(pendingRef.current[stepId] || {}) };
      }
      setSaveFailed(true);
    }
  }, [saveProgress]);

  const handleDataChange = (data: Record<string, unknown>) => {
    const currentStep = session?.steps[activeStep];
    if (!currentStep) return;

    setStepData((prev) => ({
      ...prev,
      [currentStep.id]: { ...(prev[currentStep.id] || {}), ...data },
    }));

    // Buffer and save once typing pauses. Saving per keystroke burns the public
    // endpoint's anonymous rate limit and loses the tail of what was typed.
    pendingRef.current[currentStep.id] = {
      ...(pendingRef.current[currentStep.id] || {}),
      ...data,
    };
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(() => {
      void flushPending();
    }, AUTOSAVE_IDLE_MS);
  };

  // Don't leave buffered edits behind when the tab closes or the step changes.
  useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      void flushPending();
    };
  }, [flushPending]);

  const handleNext = async () => {
    const currentStep = session?.steps[activeStep];
    if (!currentStep) return;

    setIsSaving(true);
    setResumed(false);
    try {
      // Land any buffered edits before the step is marked complete.
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      await flushPending();

      // Complete the current step
      await completeStep.mutateAsync({
        stepId: currentStep.id,
        data: stepData[currentStep.id] || {},
      });

      // Move to next step
      if (activeStep < (session?.steps.length || 0) - 1) {
        setActiveStep((prev) => prev + 1);
      }

      // Refetch to get updated progress
      await refetch();
    } catch (err) {
      toast.error((err as { responseData?: unknown })?.responseData ? JSON.stringify((err as { responseData: unknown }).responseData) : 'Failed to save progress. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    setResumed(false);
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // The button stays mounted after a successful submit; without this a second
    // tap re-posts the whole session.
    if (submitted || isSaving) return;

    setIsSaving(true);
    try {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      await flushPending();

      const submission = await submitOnboarding.mutateAsync(undefined);
      setSubmitted(true);
      toast.success(submission.message);
      await refetch();

      // Show the completion screen. Submitting moves the session to
      // `awaiting_review`, not `completed`, so waiting on status would never
      // advance and the hire would sit on Review with no confirmation.
      const completionIndex = session?.steps.findIndex((s) => s.type === 'completion') ?? -1;
      if (completionIndex >= 0) {
        setActiveStep(completionIndex);
      }
    } catch {
      toast.error('Failed to submit onboarding. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.surface',
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-md" sx={{ mt: 2 }}>
          Loading your onboarding...
        </Typography>
      </Box>
    );
  }

  if (error || !session) {
    const status = (error as { status?: number } | null)?.status;
    const isExpired = status === 410;
    const isRevoked = status === 403;
    const isNotFound = status === 404;

    const heading = isExpired
      ? 'Link Expired'
      : isRevoked
      ? 'Access Revoked'
      : isNotFound
      ? 'Onboarding Not Found'
      : 'Unable to Access Onboarding';

    const message = isExpired
      ? 'Your onboarding link has expired. Please contact HR to request a new invitation.'
      : isRevoked
      ? 'Your onboarding access has been revoked. Please contact HR for assistance.'
      : isNotFound
      ? "We couldn't find this onboarding link. Please double-check the link, or contact HR for a new invitation."
      : 'Something went wrong loading your onboarding. Please try again in a moment — if the problem continues, contact HR.';

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.surface',
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 500, textAlign: 'center', p: 4 }}>
          <ErrorOutlined size={64} color="var(--joy-palette-danger-500)" style={{ marginBottom: 16 }} />
          <Typography level="h3" sx={{ mb: 1 }}>
            {heading}
          </Typography>
          <Typography level="body-md" color="neutral" sx={{ mb: 3 }}>
            {message}
          </Typography>
          <Alert color="neutral" variant="soft">
            If you need assistance, please contact your HR administrator.
          </Alert>
        </Card>
      </Box>
    );
  }

  if (session.status === 'completed') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.surface',
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 500, textAlign: 'center', p: 4 }}>
          <CheckCircleOutlined size={64} color="var(--joy-palette-success-500)" style={{ marginBottom: 16 }} />
          <Typography level="h3" sx={{ mb: 1 }}>
            Onboarding Complete!
          </Typography>
          <Typography level="body-md" color="neutral" sx={{ mb: 3 }}>
            Thank you for completing your onboarding, {session.team_member_first_name}! Your onboarding is approved and your team member record is active.
          </Typography>
          <Alert color="success" variant="soft">
            Contact your HR team for your next steps. If a new login was created, follow its separate activation instructions.
          </Alert>
        </Card>
      </Box>
    );
  }

  const currentStep = session.steps[activeStep];
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === session.steps.length - 1;
  const completionPercentage = Math.round(((activeStep) / session.steps.length) * 100);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.level1',
        pb: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'background.surface',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 2,
          px: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Typography level="h4">{session.organization_name}</Typography>
          <Typography level="body-sm" color="neutral">
            New Hire Onboarding
          </Typography>
        </Box>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ bgcolor: 'background.surface', pb: 2 }}>
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography level="body-sm" color="neutral">
              Progress
            </Typography>
            <Typography level="body-sm" fontWeight="md">
              {completionPercentage}%
            </Typography>
          </Box>
          <LinearProgress determinate value={completionPercentage} sx={{ height: 8, borderRadius: 4 }} />
        </Box>
      </Box>

      {/* Stepper (horizontal on desktop, simplified on mobile) */}
      <Box
        sx={{
          bgcolor: 'background.surface',
          py: 2,
          px: { xs: 2, md: 3 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Stepper sx={{ width: '100%' }}>
            {session.steps.map((step, index) => (
              <Step
                key={step.id}
                indicator={
                  <StepIndicator
                    variant={index < activeStep ? 'solid' : index === activeStep ? 'soft' : 'outlined'}
                    color={index < activeStep ? 'success' : index === activeStep ? 'primary' : 'neutral'}
                  >
                    {index < activeStep ? (
                      <CheckOutlined />
                    ) : (
                      stepIcons[step.type] || <PersonOutlined />
                    )}
                  </StepIndicator>
                }
              >
                <Typography
                  level="body-xs"
                  sx={{
                    color: index === activeStep ? 'primary.500' : 'neutral.500',
                    fontWeight: index === activeStep ? 'md' : 'normal',
                  }}
                >
                  {step.title}
                </Typography>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Box>

      {/* Mobile Step Indicator */}
      <Box
        sx={{
          bgcolor: 'background.surface',
          py: 2,
          px: { xs: 2, md: 3 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: { xs: 'block', md: 'none' },
        }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          <StepIndicator variant="soft" color="primary">
            {stepIcons[currentStep?.type] || <PersonOutlined />}
          </StepIndicator>
          <Box>
            <Typography level="body-sm" color="neutral">
              Step {activeStep + 1} of {session.steps.length}
            </Typography>
            <Typography level="title-md">{currentStep?.title}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Step Content */}
      <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 2, md: 3 }, mt: 3, pb: { xs: 4, md: 0 } }}>
        {!!session.corrections?.length && <Alert color="warning" sx={{ mb: 2 }}><Box><Typography level="title-md">HR requested corrections</Typography>{session.corrections.map(c => <Typography key={c.step_id}>{session.steps.find(step => step.id === c.step_id)?.title || c.step_id}: {c.comment}</Typography>)}</Box></Alert>}
        {resumed && (
          <Alert color="primary" variant="soft" sx={{ mb: 2 }}>
            Welcome back — we saved your place. Everything you entered before is still here.
          </Alert>
        )}

        {saveFailed && (
          <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
            We could not save your last change. Check your connection — we will keep
            trying, and you can keep typing.
          </Alert>
        )}

        <Card>
          {currentStep && (
            getStepComponent(
              currentStep,
              sessionState,
              handleDataChange,
              currentStep.type === 'review' ? handleSubmit : handleNext,
              handleBack,
              isFirstStep,
              isLastStep,
              session,
              sessionToken
            )
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default OnboardingWizard;
